import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../CSS/Home.css';
import TopperCard from './TopperCard';
import { getAllToppers } from '../firebase/firestoreService';

/* ── Updated with SEO-friendly paths ── */
const COURSES = [
  { icon: 'fas fa-book-open',    title: 'SSC',      subtitle: 'Maharashtra Board',    grades: 'Class V – X',      path: '/ssc-coaching-ulhasnagar-kalyan',     color: '#b8860b' },
  { icon: 'fas fa-graduation-cap', title: 'CBSE',   subtitle: 'Central Board',        grades: 'Class V – X',      path: '/cbse-coaching-ulhasnagar-kalyan',    color: '#2c5f8a' },
  { icon: 'fas fa-flask',        title: 'Science',  subtitle: 'JEE · NEET · MH-CET', grades: 'IIT · AIIMS · State', path: '/science-coaching-ulhasnagar-kalyan', color: '#1a6e4a' },
  { icon: 'fas fa-chart-line',   title: 'Commerce', subtitle: 'Class XI – XII',       grades: 'HSC · Foundation',  path: '/commerce-classes-ulhasnagar-kalyan', color: '#7b3f8c' },
];

const FEATURES = [
  { icon: 'fas fa-chalkboard-teacher', title: '30+ Expert Educators',    desc: 'Highly qualified faculty with decades of cumulative teaching experience across all academic streams and competitive examinations.' },
  { icon: 'fas fa-users',              title: '500+ Students Annually',  desc: 'A thriving academic community with structured batch sizes ensuring individual attention and consistent, measurable results.' },
  { icon: 'fas fa-medal',              title: '22 Years of Excellence',  desc: 'Established in 2004, NGT has steadily grown into the most trusted academic institution in Ulhasnagar, Maharashtra.' },
  { icon: 'fas fa-map-marker-alt',     title: '2 Convenient Branches',   desc: 'Century Branch and Khemani Branch — both strategically located for accessibility across all of Ulhasnagar.' },
  { icon: 'fas fa-book',               title: 'Curated Study Material',  desc: 'Proprietary notes, comprehensive question banks, and mock test series precisely aligned with the latest syllabi.' },
  { icon: 'fas fa-bullseye',           title: 'Career Counselling',      desc: 'Personalised one-on-one guidance sessions to help every student chart the most suitable academic and career pathway.' },
];

const STATS = [
  { num: '22+', label: 'Years of Excellence' },
  { num: '500+', label: 'Students Annually' },
  { num: '30+', label: 'Expert Faculty' },
  { num: '2', label: 'Branches' },
];

const SLIDES = [
  { img: require('../assets/NGT_LOGO_FINAL.jpg'), title: 'Welcome to NGT', sub: '22+ Years of Academic Excellence in Ulhasnagar' },
  { img: require('../assets/vivekanand.jpg'),     title: 'Inspired by Greatness', sub: 'Nurturing future leaders through quality education' },
  { img: require('../assets/SSC1.png'),           title: 'SSC Coaching',    sub: 'Maharashtra Board — Class V to X' },
  { img: require('../assets/SSC2.png'),           title: 'CBSE Classes',    sub: 'Structured learning for CBSE students' },
  { img: require('../assets/SSC3.png'),           title: 'Exam Preparation', sub: 'JEE · NEET · MH-CET · KVPY' },
  { img: require('../assets/CET1.png'),           title: 'MH-CET Excellence', sub: 'Proven results year after year' },
];

/* FAQ data — drives both the FAQ section and FAQ schema */
const FAQS = [
  {
    q: 'Which is the best coaching class in Ulhasnagar for 5th to 10th?',
    a: 'Nalanda Group Tuitions (NGT) is widely regarded as the best coaching class in Ulhasnagar for Class 5 to 10. With over 22 years of experience, expert faculty, and a proven track record for both SSC (Maharashtra State Board) and CBSE students, NGT offers the most comprehensive and result-oriented coaching in the region.',
  },
  {
    q: 'Does NGT offer JEE and NEET coaching in Ulhasnagar?',
    a: 'Yes. NGT provides dedicated JEE (IIT Mains & Advanced) and NEET coaching for Class 11 and 12 science students in Ulhasnagar. Our science programme also covers MH-CET, AIIMS, and KVPY, with experienced faculty, regular mock tests, and curated study material.',
  },
  {
    q: 'What courses does Nalanda Group Tuitions offer?',
    a: 'NGT offers coaching for: SSC Maharashtra State Board (Class 5–10), CBSE Board (Class 5–10), 11th–12th Science with JEE/NEET/MH-CET preparation, and 11th–12th Commerce (Accounts, Economics, Business Studies). Both English and Semi-English medium batches are available.',
  },
  {
    q: 'Where are the NGT branches located in Ulhasnagar?',
    a: 'NGT has two branches in Ulhasnagar: Century Branch — Deshmukh Plaza, Murbad Road, opposite Century School (421002); and Khemani Branch — Near Jhulelal School, Khemani (421004). Both branches are easily accessible from Kalyan, Ambernath, and Badlapur.',
  },
  {
    q: 'How do I get admission at Nalanda Group Tuitions?',
    a: 'You can enquire about admission by calling +91 92721 36659, emailing nalandagrouptuitions@gmail.com, or submitting an online enquiry form on this website. Admissions are open for all courses. Walk-in visits are welcome Monday to Saturday, 7:00 AM to 8:00 PM.',
  },
];

/* ─── Counter animation hook ───────────────────────────────── */
function useCountUp(target, duration = 1800, started = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!started) return;
    const numeric = parseInt(target.replace(/\D/g, ''), 10);
    if (!numeric) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setValue(Math.floor(progress * numeric));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);
  return value;
}

function StatItem({ num, label, delay }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const count = useCountUp(num, 1600, started);
  const suffix = num.replace(/[0-9]/g, '');
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setStarted(true); obs.disconnect(); } },
      { threshold: 0.5 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="ngt-stat-item" style={{ animationDelay: `${delay}ms` }}>
      <span className="stat-num">{count}{suffix}</span>
      <span className="stat-lbl">{label}</span>
    </div>
  );
}

/* ─── FAQ Component ────────────────────────────────────────── */
function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ngt-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
      <button
        className={`ngt-faq-q${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={`faq-ans-${index}`}
      >
        <span itemProp="name">{faq.q}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
      </button>
      <div
        id={`faq-ans-${index}`}
        className={`ngt-faq-a${open ? ' open' : ''}`}
        itemScope
        itemProp="acceptedAnswer"
        itemType="https://schema.org/Answer"
      >
        <p itemProp="text">{faq.a}</p>
      </div>
    </div>
  );
}

/* ─── Main Component ───────────────────────────────────────── */
const Home = () => {
  const [toppers, setToppers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    getAllToppers()
      .then(data => setToppers(data.slice(0, 8)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setActiveSlide(s => (s + 1) % SLIDES.length), 4500);
    return () => clearInterval(timer);
  }, []);

  return (
    <main itemScope itemType="https://schema.org/EducationalOrganization" className="ngt-home">
      {/* FAQ JSON-LD Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      }) }} />

      <meta itemProp="name" content="Nalanda Group Tuitions" />
      <meta itemProp="description" content="Best coaching classes in Ulhasnagar for SSC, CBSE (Class 5–10), JEE, NEET, MH-CET and Commerce (11th–12th). 22+ years of excellence." />
      <meta itemProp="url" content="https://nalandagrouptuitions.com" />
      <meta itemProp="telephone" content="+919272136659" />

      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="ngt-hero" aria-label="Welcome to Nalanda Group Tuitions — Best Coaching in Ulhasnagar">
        <div className="hero-slider">
          {SLIDES.map((slide, i) => (
            <div key={i} className={`hero-slide ${i === activeSlide ? 'active' : ''}`} aria-hidden={i !== activeSlide}>
              <img src={slide.img} alt={`${slide.title} - Nalanda Group Tuitions Ulhasnagar`} loading={i === 0 ? 'eager' : 'lazy'} className="hero-img" />
              <div className="hero-overlay" />
            </div>
          ))}
          <div className="hero-content">
            <div className="hero-badge">Est. 2004 · Ulhasnagar, Maharashtra</div>
            <h1 className="hero-title" itemProp="name">
              Nalanda Group<br />
              <span className="hero-title-accent">Tuitions</span>
            </h1>
            <p className="hero-tagline">{SLIDES[activeSlide].sub}</p>
            <div className="hero-actions">
              <Link to="/enquiry" className="btn-hero-primary">Enquire Now</Link>
              <Link to="/about" className="btn-hero-secondary">About NGT</Link>
            </div>
          </div>
          <div className="hero-dots" role="tablist" aria-label="Slide navigation">
            {SLIDES.map((_, i) => (
              <button key={i} role="tab" aria-selected={i === activeSlide} aria-label={`Go to slide ${i + 1}`}
                className={`hero-dot ${i === activeSlide ? 'active' : ''}`} onClick={() => setActiveSlide(i)} />
            ))}
          </div>
          <button className="hero-arrow left" aria-label="Previous slide" onClick={() => setActiveSlide(s => (s - 1 + SLIDES.length) % SLIDES.length)}>
            <i className="fas fa-chevron-left" />
          </button>
          <button className="hero-arrow right" aria-label="Next slide" onClick={() => setActiveSlide(s => (s + 1) % SLIDES.length)}>
            <i className="fas fa-chevron-right" />
          </button>
        </div>
        <div className="hero-scroll-hint" aria-hidden="true"><span>Scroll</span><div className="scroll-line" /></div>
      </section>

      {/* ── Stats Bar ─────────────────────────────────────── */}
      <section className="ngt-stats-bar" aria-label="NGT at a Glance">
        <div className="container">
          <div className="stats-grid">
            {STATS.map((s, i) => <StatItem key={i} num={s.num} label={s.label} delay={i * 120} />)}
          </div>
        </div>
      </section>

      {/* ── Intro Strip ───────────────────────────────────── */}
      <section className="ngt-intro" aria-label="About Nalanda Group Tuitions">
        <div className="container">
          <div className="intro-inner">
            <div className="intro-badge-col">
              <div className="intro-year-badge">
                <span className="year-num">22</span>
                <span className="year-text">Years of<br />Excellence</span>
              </div>
            </div>
            <div className="intro-text-col">
              <h2 className="intro-heading">Shaping Academic Futures<br /><em>Since 2004</em></h2>
              <p itemProp="description">
                <strong>Nalanda Group Tuitions</strong> is Ulhasnagar's most trusted coaching institute.
                With two well-equipped branches, 30+ expert faculty, and a student-first philosophy,
                NGT has guided thousands of students towards academic and professional success.
              </p>
              <p>
                Our structured programmes — spanning <Link to="/ssc-coaching-ulhasnagar-kalyan">SSC board coaching</Link>,{' '}
                <Link to="/cbse-coaching-ulhasnagar-kalyan">CBSE coaching</Link>,{' '}
                <Link to="/science-coaching-ulhasnagar-kalyan">competitive science (JEE/NEET/MH-CET)</Link>, and{' '}
                <Link to="/commerce-classes-ulhasnagar-kalyan">commerce classes</Link> — build conceptual clarity and examination confidence.
              </p>
              <Link to="/about" className="btn-intro-link">Read Our Story <i className="fas fa-arrow-right" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Courses ───────────────────────────────────────── */}
      <section className="ngt-courses" aria-labelledby="courses-heading">
        <div className="container">
          <header className="section-header">
            <span className="section-tag">Academic Programmes</span>
            <h2 className="section-title" id="courses-heading">Our Coaching Programmes</h2>
            <p className="section-sub">Comprehensive coaching from foundational levels through competitive examinations</p>
          </header>
          <div className="courses-grid">
            {COURSES.map((c, i) => (
              <Link key={i} to={c.path} className="course-card"
                aria-label={`${c.title} coaching in Ulhasnagar`} style={{ '--card-accent': c.color }}>
                <div className="course-card-inner">
                  <div className="course-icon-wrap"><i className={c.icon} aria-hidden="true" /></div>
                  <div className="course-body">
                    <h3 className="course-title">{c.title}</h3>
                    <p className="course-sub">{c.subtitle}</p>
                    <span className="course-grades">{c.grades}</span>
                  </div>
                  <div className="course-arrow"><i className="fas fa-arrow-right" aria-hidden="true" /></div>
                </div>
                <div className="course-accent-bar" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why NGT ───────────────────────────────────────── */}
      <section className="ngt-why" aria-labelledby="why-heading">
        <div className="container">
          <header className="section-header light">
            <span className="section-tag light">Our Strengths</span>
            <h2 className="section-title light" id="why-heading">Why Families Choose NGT</h2>
            <p className="section-sub light">Trusted by thousands of families across Ulhasnagar and Kalyan for over two decades</p>
          </header>
          <div className="why-grid">
            {FEATURES.map((f, i) => (
              <article key={i} className="why-card" aria-label={f.title}>
                <div className="why-icon"><i className={f.icon} aria-hidden="true" /></div>
                <h3 className="why-title">{f.title}</h3>
                <p className="why-desc">{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Toppers ───────────────────────────────────────── */}
      {!loading && toppers.length > 0 && (
        <section className="ngt-toppers" aria-labelledby="toppers-heading">
          <div className="container">
            <header className="section-header">
              <span className="section-tag">Student Success</span>
              <h2 className="section-title" id="toppers-heading">Our Proud Achievers</h2>
              <p className="section-sub">Students who excelled through dedication and the guidance of NGT faculty</p>
            </header>
            <div className="topper-scroll" role="list" aria-label="List of top achievers">
              {toppers.map((t, i) => <div key={t.id || i} role="listitem"><TopperCard topper={t} /></div>)}
            </div>
          </div>
        </section>
      )}

      {/* ── Testimonial Strip ─────────────────────────────── */}
      <section className="ngt-testimonial-strip" aria-label="Parent Testimonial">
        <div className="container">
          <div className="testimonial-inner">
            <i className="fas fa-quote-left testimonial-quote-icon" aria-hidden="true" />
            <blockquote>
              <p>"NGT transformed my daughter's approach to studying. The faculty's dedication and structured methodology gave her the confidence she needed to excel in her board examinations."</p>
              <cite>— Parent of a Class X SSC Topper, 2024</cite>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          SEO CONTENT SECTION — placed below UI, above CTA
          Design: clean card, no keyword stuffing
      ══════════════════════════════════════════════════════ */}
      <section className="ngt-seo-content" aria-labelledby="seo-about-heading">
        <div className="container">
          <div className="seo-content-inner">
            <h2 id="seo-about-heading">
              Best Coaching Classes in Ulhasnagar &amp; Kalyan — Nalanda Group Tuitions
            </h2>
            <p>
              Nalanda Group Tuitions (NGT), founded in 2004, has established itself as the most trusted
              coaching institute in Ulhasnagar, Kalyan, Ambernath and surrounding areas of Thane district.
              Whether you are looking for <Link to="/ssc-coaching-ulhasnagar-kalyan">SSC coaching for Class 5 to 10</Link> in
              Maharashtra State Board, <Link to="/cbse-coaching-ulhasnagar-kalyan">CBSE classes for Class 5 to 10</Link>,
              or comprehensive <Link to="/science-coaching-ulhasnagar-kalyan">JEE, NEET and MH-CET coaching</Link> for
              Class 11 and 12 science students — NGT has a proven programme tailored to your goals.
            </p>
            <p>
              Our <Link to="/commerce-classes-ulhasnagar-kalyan">best commerce classes for 11th and 12th</Link> cover
              Accounts, Economics, and Business Studies with structured notes and regular assessments.
              With 30+ experienced educators, two conveniently located branches, and a reputation built
              over 22+ years, NGT remains the first choice for academic coaching in Ulhasnagar and Kalyan.
            </p>
            <div className="seo-keyword-chips" aria-label="Courses offered">
              <Link to="/ssc-coaching-ulhasnagar-kalyan" className="seo-chip">SSC Coaching Ulhasnagar</Link>
              <Link to="/cbse-coaching-ulhasnagar-kalyan" className="seo-chip">CBSE Classes Ulhasnagar</Link>
              <Link to="/science-coaching-ulhasnagar-kalyan" className="seo-chip">JEE Coaching Ulhasnagar</Link>
              <Link to="/science-coaching-ulhasnagar-kalyan" className="seo-chip">NEET Coaching Ulhasnagar</Link>
              <Link to="/science-coaching-ulhasnagar-kalyan" className="seo-chip">MHT-CET Coaching</Link>
              <Link to="/commerce-classes-ulhasnagar-kalyan" className="seo-chip">Commerce Classes 11th 12th</Link>
              <Link to="/ssc-coaching-ulhasnagar-kalyan" className="seo-chip">Best Classes 5 to 10 Kalyan</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FAQ SECTION — targets question-based searches
      ══════════════════════════════════════════════════════ */}
      <section className="ngt-faq" aria-labelledby="faq-heading"
        itemScope itemType="https://schema.org/FAQPage">
        <div className="container">
          <header className="section-header">
            <span className="section-tag">Common Questions</span>
            <h2 className="section-title" id="faq-heading">Frequently Asked Questions</h2>
            <p className="section-sub">Everything you need to know about admissions and courses at NGT</p>
          </header>
          <div className="ngt-faq-list">
            {FAQS.map((faq, i) => <FAQItem key={i} faq={faq} index={i} />)}
          </div>
          <div className="text-center mt-4">
            <Link to="/enquiry" className="btn-intro-link">
              Have more questions? Contact us <i className="fas fa-arrow-right" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="ngt-cta" aria-label="Enrol at Nalanda Group Tuitions">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-text">
              <h2 className="cta-heading">Begin Your Journey to<br />Academic Excellence</h2>
              <p className="cta-sub">
                Contact us today for admissions, detailed course information, and a free consultation
                with our expert academic counsellors in Ulhasnagar and Kalyan.
              </p>
            </div>
            <div className="cta-actions">
              <Link to="/enquiry" className="btn-cta-primary">Submit an Enquiry</Link>
              <a href="tel:+919272136659" className="btn-cta-secondary" aria-label="Call Nalanda Group Tuitions">
                <i className="fas fa-phone" aria-hidden="true" /> +91 92721 36659
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
